import {
  OfficeName,
  PracticeQueryParams,
  RevisionStatus,
} from 'constants/application-constants'

import LockIcon from 'assets/Icons/LockIcon'
import {
  Badge,
  Breadcrumbs,
  Button,
  DropdownItem,
  IconContainer,
  Typography,
} from 'components/common'
import { useAppSelector, useIsBelowBreakpoint } from 'hooks'
import { t } from 'i18next'
import { ChevronLeft, Folder, Paperclip } from 'react-feather'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import styles from './BackOfficeHeader.module.scss'
import { MoreVert, NewFile, Plus } from '../../../../../assets/Icons'
import { BreadcrumbProps } from '../../../../common/Breadcrumbs/Breadcrumb.type'
import ButtonPopover from '../../../../common/ButtonPopover/ButtonPopover'
import { selectCurrentRevision } from '../../api'
import CollaboratorsButton from '../CollaboratorsButton/CollaboratorsButton'
import MetadataLanguageDropdown from '../MetadataLanguageDropdown/MetadataLanguageDropdown'
import PreCheckStatusInfo from '../PreCheckStatusInfo/PreCheckStatusInfo'
import RevisionSelect from '../RevisionSelect/RevisionSelect'

const BackOfficeHeader = (): JSX.Element => {
  const currentRevision = useAppSelector(selectCurrentRevision)
  const navigate = useNavigate()
  const isTabLand = useIsBelowBreakpoint('tabLand')
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const currentMetadata = currentRevision?.metadata.find(
    ({ language }) =>
      searchParams.get(PracticeQueryParams.METADATA_LANGUAGE) === language.code
  )
  const revisionStatus = searchParams.get(PracticeQueryParams.REVISION_STATUS)

  const handleViewAsEnduserClick = () => {
    const frontOfficeUrl = `/${OfficeName.FRONT}/practices/${currentRevision?.practice_id}`

    navigate({
      ...location,
      pathname: frontOfficeUrl,
      search: `?${PracticeQueryParams.REVISION_STATUS}=${RevisionStatus.PUBLISHED}`,
    })
  }

  const crumbs: BreadcrumbProps[] = [
    {
      href: '/practices',
      title: 'My Guidelines',
      icon: <Folder />,
      iconOnly: true,
      tooltip: t('home.backToGuideline'),
      styleInverted: true,
    },
  ]

  if (currentRevision && currentMetadata)
    crumbs.push({
      href: location,
      title: currentMetadata?.title ?? t('practice.current'),
      tag: <RevisionSelect />,
      styleInverted: true,
    })

  const handleSidebarOpen = () => {
    navigate(
      {
        ...location,
        hash: 'sidebar',
      },
      { replace: true }
    )
  }

  const handleNavigateBack = () => {
    navigate(-1)
  }

  return (
    <div className={styles.root}>
      {isTabLand && (
        <>
          <Button
            size="small"
            variant="secondary"
            color="dark"
            icon={<ChevronLeft />}
            onClick={handleNavigateBack}
            className={styles.backButton}
            aria-label={t('frontOffice.ariaSidebarBtn') ?? ''}
            as="button"
          />

          <RevisionSelect />
        </>
      )}
      <nav className={styles.nav}>
        <Breadcrumbs crumbs={crumbs} styleInverted={true} />
      </nav>
      <div className={styles.main} />
      <div className={styles.actions}>
        <MetadataLanguageDropdown />
        {/* TODO: "More" Dropdown */}

        {!!revisionStatus &&
          revisionStatus === RevisionStatus.PUBLISHED &&
          currentRevision?.practice_id && (
            <Button
              variant="secondary"
              color="dark"
              size="small"
              onClick={handleViewAsEnduserClick}
            >
              {t('practice.viewAsEnduser')}
            </Button>
          )}

        {!!revisionStatus && revisionStatus !== RevisionStatus.DRAFT && (
          <Badge size="small" color="primary" className={styles.badge}>
            <IconContainer
              className={styles.icon}
              size="x-small"
              icon={<LockIcon />}
            />
            {t('practice.viewOnly')}
          </Badge>
        )}
        {!!revisionStatus && revisionStatus === RevisionStatus.DRAFT && (
          <>
            <CollaboratorsButton />
            <PreCheckStatusInfo />
            {!isTabLand && (
              <ButtonPopover
                buttonProps={{
                  variant: 'secondary',
                  icon: <Plus />,
                  className: styles.popOverButton,
                  children: t('practices.newMaterial'),
                }}
                // className={styles.listItems}
                anchorOrigin={{
                  horizontal: 'center',
                  vertical: 'bottom',
                }}
                transformOrigin={{
                  horizontal: 'center',
                  vertical: 'top',
                }}
              >
                <DropdownItem
                  onClick={() =>
                    navigate({
                      ...location,
                      pathname: 'upload',
                    })
                  }
                  icon={<NewFile />}
                >
                  <Typography element="span">
                    {t('practices.newDocument')}
                  </Typography>
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    navigate({
                      ...location,
                      pathname: 'upload-material',
                    })
                  }
                  icon={<Paperclip />}
                >
                  <Typography element="span">
                    {t('practices.newMaterial')}
                  </Typography>
                  <br />
                  <Typography element="span" size={'small'} color={'label'}>
                    {t('practices.newMaterialSubtitle')}
                  </Typography>
                </DropdownItem>
              </ButtonPopover>
            )}
            {isTabLand && (
              <Button
                size="small"
                variant="subtle"
                icon={<MoreVert />}
                onClick={handleSidebarOpen}
                className={styles.sidebarBtn}
                aria-label={t('frontOffice.ariaSidebarBtn') ?? ''}
                as="button"
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default BackOfficeHeader
